import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Lock } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const REASON_COPY = {
  not_found:    "This reset link isn't valid. It may have been superseded by a newer one.",
  already_used: "This reset link has already been used. Request a new one if you still need it.",
  expired:      "This reset link has expired. Request a new one to try again.",
  malformed:    "This reset link is malformed. Request a new one to try again.",
};

export default function AdminResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inspect, setInspect] = useState(null); // { valid, email_hint? } | { valid:false, reason }
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${BASE}/admin/reset-password/inspect/${token}`)
      .then((r) => setInspect(r.data))
      .catch(() => setInspect({ valid: false, reason: "not_found" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2)    { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/admin/reset-password`, { token, new_password: pw });
      // Signed in on success — same storage keys AdminLogin uses.
      localStorage.setItem("sw_admin_token", data.token);
      localStorage.setItem("sw_admin", JSON.stringify(data.admin));
      toast.success("Password updated");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-3xl font-black text-white tracking-tight mb-1">Brainydo</div>
          <div className="text-slate-400 text-sm font-medium tracking-wide uppercase">Super Admin</div>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          {!inspect ? (
            <div className="text-center text-slate-400 text-[13px] py-4">
              <Loader2 size={18} className="animate-spin mx-auto mb-2" />
              Verifying link…
            </div>
          ) : !inspect.valid ? (
            <>
              <div className="w-11 h-11 rounded-full bg-red-50 grid place-items-center mb-4">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <h1 className="text-[17px] font-bold text-slate-900 mb-2">Reset link unavailable</h1>
              <p className="text-[13px] text-slate-600 leading-relaxed">
                {REASON_COPY[inspect.reason] || "This reset link is not valid."}
              </p>
              <div className="mt-6 flex gap-2">
                <Link
                  to="/admin/forgot-password"
                  className="h-9 px-4 inline-flex items-center bg-slate-900 hover:bg-slate-700 text-white text-[12.5px] font-semibold rounded-xl transition"
                >
                  Request a new link
                </Link>
                <Link
                  to="/admin/login"
                  className="h-9 px-4 inline-flex items-center border border-slate-200 hover:border-slate-400 text-[12.5px] rounded-xl transition"
                >
                  Back to sign in
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-blue-50 grid place-items-center mb-4">
                <Lock size={18} className="text-blue-600" />
              </div>
              <h1 className="text-[17px] font-bold text-slate-900 mb-1.5">Choose a new password</h1>
              {inspect.email_hint && (
                <p className="text-[13px] text-slate-500 mb-5">for <strong>{inspect.email_hint}</strong></p>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">New password</label>
                  <input
                    type="password"
                    required
                    autoFocus
                    minLength={6}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Confirm password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className={
                      "w-full h-10 px-3 border rounded-xl text-[13.5px] focus:outline-none focus:ring-2 focus:ring-slate-900 " +
                      (pw2 && pw !== pw2 ? "border-red-400" : "border-slate-200")
                    }
                  />
                  {pw2 && pw !== pw2 && (
                    <p className="text-[11.5px] text-red-600 mt-1">Passwords don't match.</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={busy || pw.length < 6 || pw !== pw2}
                  className="w-full h-10 inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl text-[13.5px] font-semibold hover:bg-slate-700 disabled:opacity-50 transition active:scale-95"
                >
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {busy ? "Setting new password…" : "Set new password & sign in"}
                </button>
              </form>
            </>
          )}
        </div>
        <p className="text-center text-slate-600 text-xs mt-6">Restricted access — authorised personnel only</p>
      </div>
    </div>
  );
}
